import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
    ODataQuery,
    useListContext,
    UseListContextReturn,
} from './useListContext';
import useTimeZone from '../../customHooks/useTimeZone';
import {
    IFilterOptionState,
    sortDirections,
} from '../../types/lists/listTypes';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import { IComplementaryDataState } from '../../types/taxes/taxesTypes';
import { useODataMultipleQueries } from '../../customHooks/useODataMultipleQueries';
import { IStaticRateContract } from '../../types/entities/IStaticRateContract';
import {
    ContractsListViewType,
    IContractListFiltersState,
    IContractListState,
} from '../../types/contracts/contractTypes';
import { getContractsInitialStateFilters } from '../../utils/contractUtils/contractsListUtils';
import { adaptStaticRateContractToState } from '../../services/contracts/contractsAdapters';
import { IAgencyHotelRelationship } from '../../types/entities/IAgencyHotelRelationship';

interface ContractsListContextType
    extends UseListContextReturn<
        IContractListState,
        IContractListFiltersState
    > {
    visibleView: ContractsListViewType;
    changeVisibleView: (view: ContractsListViewType) => void;
    supplierHotels: IComplementaryDataState;
    agencies: IComplementaryDataState;
}

const ContextContractsList = createContext<
    ContractsListContextType | undefined
>(undefined);

export const ProviderContractsList = ({
    children,
}: {
    children: ReactNode;
}) => {
    const timezone = useTimeZone();
    const INITIAL_STATE_FILTERS = getContractsInitialStateFilters(timezone);
    const ITEMS_PER_PAGE = 10;
    const {
        items,
        filters,
        sortDir,
        sortedBy,
        handleFilterChange,
        resetFilters,
        sortByField,
        handleSearch,
        handleClearFilters,
        handleScroll,
        isLoading,
        totalResults,
        currentResults,
        page,
    } = useListContext<
        IStaticRateContract,
        IContractListState,
        IContractListFiltersState
    >({
        initialFilters: INITIAL_STATE_FILTERS,
        buildQuery: (filtersToOdata): ODataQuery => {
            return {
                entityName: readOnlyEntities.StaticRateContract,
                query: {
                    filter: filtersToOdata,
                    select: [
                        'Id',
                        'Enabled',
                        'Name',
                        'IdAgency',
                        'Agency/Name',
                        'IdSupplierHotel',
                        'SupplierHotel/Name',
                        'IdSupplierHotelRatePlan',
                        'SupplierHotelRatePlan/Name',
                        'IdRateScopeModel',
                        'RateScopeModel/LocalizedCaption',
                        'RateScopeModel/Caption',
                        'RateScopeModel/Name',
                        'RateScopeModel/Key',
                        'Description',
                        'ActiveFrom',
                        'ActiveTo',
                        'StaticRateContractStatus/IdStaticRateContractStatusType',
                        'StaticRateContractStatus/StaticRateContractStatusType/LocalizedCaption',
                        'StaticRateContractStatus/StaticRateContractStatusType/Caption',
                        'StaticRateContractStatus/StaticRateContractStatusType/Name',
                        'StaticRateContractStatus/StaticRateContractStatusType/Color',
                    ],
                    expand: [
                        readOnlyEntities.Agency,
                        readOnlyEntities.SupplierHotel,
                        readOnlyEntities.SupplierHotelRatePlan,
                        readOnlyEntities.RateScopeModel,
                        `${readOnlyEntities.StaticRateContractStatus}/${readOnlyEntities.StaticRateContractStatusType}`,
                    ],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: ITEMS_PER_PAGE,
                },
            };
        },
        adaptItem: adaptStaticRateContractToState,
        itemsPerPage: ITEMS_PER_PAGE,
        initialSortBy: 'DateLastUpdated',
        initialSortDir: sortDirections.DESC,
    });

    const [visibleView, setVisibleView] = useState<ContractsListViewType>(
        ContractsListViewType.TABLE
    );
    const changeVisibleView = (view: ContractsListViewType) => {
        setVisibleView(view);
    };

    const independentQueries = [
        {
            key: 'supplierHotels',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotel,
                select: ['Id', 'Name', 'IdCurrency'],
                orderBy: 'Name asc',
            },
        },
    ];

    const { supplierHotels } = useODataMultipleQueries({
        queries: independentQueries,
        dependencies: [],
    });

    const supplierHotelSelected = filters.IdSupplierHotel
        .value as IFilterOptionState;
    const IdSupplierHotel = supplierHotelSelected?.Id
        ? parseInt(supplierHotelSelected.Id)
        : null;

    const dependentQueries = [
        {
            key: 'agencies',
            endpoint: {
                entityName: readOnlyEntities.AgencyHotelRelationship,
                select: 'Agency/Id,Agency/Name',
                orderBy: 'Agency/Name',
                expand: ['Agency'],
                filter: [`IdSupplierHotel eq ${IdSupplierHotel}`],
            },
            transform: (data: IAgencyHotelRelationship[]) =>
                data.map(relationship => ({
                    Id: relationship.Agency.Id,
                    Name: relationship.Agency.Name,
                })),
        },
    ];

    const { agencies } = useODataMultipleQueries({
        queries: dependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!supplierHotelSelected,
    });

    return (
        <ContextContractsList.Provider
            value={{
                items,
                filters,
                sortDir,
                sortedBy,
                handleFilterChange,
                sortByField,
                handleSearch,
                handleClearFilters,
                handleScroll,
                isLoading,
                totalResults,
                currentResults,
                page,
                visibleView,
                resetFilters,
                changeVisibleView,
                supplierHotels,
                agencies,
            }}
        >
            {children}
        </ContextContractsList.Provider>
    );
};

export const useContractsList = () => {
    const context = useContext(ContextContractsList);
    if (context === undefined) {
        throw new Error(
            'useContractsList must be used within a ProviderContractsList'
        );
    }
    return context;
};

export const useContractsComplementaryData = () => {
    const { supplierHotels, agencies } = useContext(
        ContextContractsList
    ) as ContractsListContextType;

    return {
        supplierHotels,
        agencies,
    };
};
